import { Component } from '@theme/component';
function normalizeSelection(selection) {
  const tags = (values) => [...new Set((values ?? []).map(String).map((value) => value.trim()).filter(Boolean))];
  const budgets = (selection.budgets ?? []).map(({ min, max }) => {
    const lower = Number(min);
    const upper = max === null || max === undefined ? Infinity : Number(max);
    if (!Number.isSafeInteger(lower) || lower < 0 || !(upper === Infinity || Number.isSafeInteger(upper)) || upper <= lower) {
      throw new TypeError('Invalid budget range');
    }
    return { min: lower, max: upper };
  });
  return { recipientTags: tags(selection.recipientTags), ageTags: tags(selection.ageTags), budgets };
}

function qualifyingVariant(product, rawSelection) {
  const selection = normalizeSelection(rawSelection);
  if (!selection.ageTags.length) return null;
  const tags = new Set(product.tags ?? []);
  if (selection.recipientTags.length && !selection.recipientTags.some((tag) => tags.has(tag))) return null;
  if (!selection.ageTags.some((tag) => tags.has(tag))) return null;
  if (tags.has('hidden-from-catalog')) return null;
  return (product.variants ?? []).filter((variant) => {
    if (!variant.available || !Number.isSafeInteger(variant.price) || variant.price < 0) return false;
    return !selection.budgets.length || selection.budgets.some(({ min, max }) => variant.price >= min && variant.price < max);
  }).sort((a, b) => a.price - b.price)[0] ?? null;
}

async function collectCatalogPages(firstPage, loadPage, { signal, maxPages = 500 } = {}) {
  const products = new Map();
  const visited = new Set();
  let page = firstPage;
  let pages = 0;
  while (page) {
    signal?.throwIfAborted();
    if (++pages > maxPages || !Array.isArray(page.products)) throw new Error('Incomplete catalog');
    for (const product of page.products) {
      if (product.id === undefined) throw new Error('Missing product identity');
      products.set(String(product.id), product);
    }
    if (!page.next) break;
    if (visited.has(page.next)) throw new Error('Pagination loop');
    visited.add(page.next);
    page = await loadPage(page.next, signal);
    if (!page) throw new Error('Incomplete catalog');
  }
  return [...products.values()];
}


class LucaGiftFinder extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.form = this.querySelector('form');
    this.stage = 0;
    const signal = this.controller.signal;
    this.form.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      this.refresh();
      if (input.name === 'recipient') this.showStage(1);
    }, { signal });
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-back')) this.showStage(Math.max(0, this.stage - 1));
      if (button.hasAttribute('data-next') && this.form.querySelector('[name="age"]:checked')) this.showStage(2);
      if (button.hasAttribute('data-reset')) { this.form.reset(); this.refresh(); this.showStage(0); }
      if (button.hasAttribute('data-skip')) {
        for (const input of this.form.querySelectorAll('[name="budget"]')) input.checked = false;
        this.form.requestSubmit();
      }
    }, { signal });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const recipient = this.form.querySelector('[name="recipient"]:checked');
      const ages = [...this.form.querySelectorAll('[name="age"]:checked')];
      if (!recipient) { this.showStage(0); return; }
      if (!ages.length) { this.showStage(1); return; }
      const target = new URL(this.dataset.url, location.origin);
      target.searchParams.set('view', 'luca-gifts');
      for (const tag of this.tags(recipient)) target.searchParams.append('lg_recipient', tag);
      for (const age of ages) for (const tag of this.tags(age)) target.searchParams.append('lg_age', tag);
      for (const input of this.form.querySelectorAll('[name="budget"]:checked')) target.searchParams.append('lg_budget', `${Math.round(Number(input.dataset.min) * 100)}:${Number(input.dataset.max) > 0 ? Math.round(Number(input.dataset.max) * 100) : ''}`);
      target.searchParams.set('lg_label', this.summary());
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) target.searchParams.set('preview_theme_id', preview);
      location.assign(target.href);
    }, { signal });
    this.refresh();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
    super.disconnectedCallback();
  }
  tags(input) { return (input.dataset.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean); }
  summary() { return [...this.form.querySelectorAll('input:checked')].map((input) => input.closest('label').querySelector('.lgf__label').textContent.trim()).join(' · '); }
  refresh() {
    this.querySelector('.lgf__summary').textContent = this.summary();
    this.querySelector('[data-next]').disabled = !this.form.querySelector('[name="age"]:checked');
    this.querySelector('[data-reset]').hidden = !this.form.querySelector('input:checked');
  }
  showStage(stage) {
    this.stage = stage;
    for (const panel of this.querySelectorAll('[data-step]')) panel.hidden = Number(panel.dataset.step) !== stage;
    [...this.querySelectorAll('.lgf__progress li')].forEach((item, index) => {
      item.removeAttribute('aria-current');
      item.toggleAttribute('data-complete', index < stage);
      if (index === stage) item.setAttribute('aria-current', 'step');
    });
    this.querySelector(`[data-step="${stage}"] legend`).focus({ preventScroll: true });
  }
}

class LucaGiftResults extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-more')) this.appendResults();
      if (button.hasAttribute('data-retry')) this.loadResults();
    }, { signal: this.controller.signal });
    this.loadResults();
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
    super.disconnectedCallback();
  }
  async loadResults() {
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    const status = this.querySelector('.lgf__status');
    const grid = this.querySelector('.lgf__grid');
    const retry = this.querySelector('[data-retry]');
    status.textContent = status.dataset.loading;
    grid.replaceChildren();
    retry.hidden = true;
    this.querySelector('[data-more]').hidden = true;
    const params = new URL(location.href).searchParams;
    this.querySelector('.lgf__chosen').textContent = params.get('lg_label') || '';
    try {
      const selection = normalizeSelection({ recipientTags: params.getAll('lg_recipient'), ageTags: params.getAll('lg_age'), budgets: params.getAll('lg_budget').map((range) => { const [min, max] = range.split(':'); return { min: Number(min), max: max === '' ? null : Number(max) }; }) });
      if (!selection.ageTags.length) { status.textContent = status.dataset.empty; return; }
      const readPage = (root) => {
        const template = root.querySelector('template[data-gift-catalog]');
        if (!template) throw new Error('Missing catalog response');
        const products = [...template.content.querySelectorAll('[data-gift-product]')].map((card) => ({ id: card.dataset.id, tags: JSON.parse(card.dataset.tags), card, variants: [...card.querySelector('template[data-gift-variants]').content.querySelectorAll('template[data-gift-variant]')].map((variant) => ({ id: variant.dataset.id, price: Number(variant.dataset.price), available: variant.dataset.available === 'true', priceTemplate: variant })) }));
        return { products, next: template.dataset.next || null, total: Number(template.dataset.total) };
      };
      const first = readPage(this);
      if (first.total > 25000) throw new Error('Catalog exceeds supported pagination');
      const products = await collectCatalogPages(first, async (next, signal) => {
        const url = new URL(location.href);
        url.searchParams.set('page', new URL(next, location.origin).searchParams.get('page') || '1');
        url.searchParams.set('section_id', this.closest('[id^="shopify-section-"]').id.replace('shopify-section-', ''));
        const response = await fetch(url, { signal });
        if (!response.ok) throw new Error('Catalog request failed');
        return readPage(new DOMParser().parseFromString(await response.text(), 'text/html'));
      }, { signal: controller.signal });
      if (products.length !== first.total) throw new Error('Incomplete catalog');
      if (controller.signal.aborted) return;
      this.matches = products.flatMap((product) => { const variant = qualifyingVariant(product, selection); return variant ? [{ product, variant }] : []; });
      this.rendered = 0;
      this.pageSize = Number(this.querySelector('template[data-gift-catalog]').dataset.pageSize) || 24;
      status.textContent = this.matches.length ? `${this.matches.length} ${status.dataset.count}` : status.dataset.empty;
      this.appendResults();
    } catch (error) {
      if (controller.signal.aborted) return;
      status.textContent = status.dataset.error;
      retry.hidden = false;
      grid.replaceChildren();
    }
  }
  appendResults() {
    if (!this.matches) return;
    const grid = this.querySelector('.lgf__grid');
    const fragment = document.createDocumentFragment();
    for (const { product, variant } of this.matches.slice(this.rendered, this.rendered + this.pageSize)) {
      const card = product.card.cloneNode(true);
      for (const link of card.querySelectorAll('a[href]')) {
        const url = new URL(link.getAttribute('href'), location.origin);
        if (url.origin === location.origin && url.pathname.includes('/products/')) {
          url.searchParams.set('variant', variant.id);
          link.href = url.href;
        }
      }
      const priceContainer = card.querySelector('product-price [ref="priceContainer"]');
      if (priceContainer) priceContainer.replaceWith(variant.priceTemplate.content.cloneNode(true));
      for (const input of card.querySelectorAll('input[name="id"]')) input.value = variant.id;
      const nativeCard = card.querySelector('product-card');
      if (nativeCard) nativeCard.id = `${nativeCard.id}-gift-${product.id}`;
      card.dataset.giftVariant = variant.id;
      card.dataset.giftPrice = String(variant.price);
      card.removeAttribute('data-tags');
      card.querySelector('template[data-gift-variants]').remove();
      fragment.append(card);
    }
    grid.append(fragment);
    this.rendered = Math.min(this.matches.length, this.rendered + this.pageSize);
    this.querySelector('[data-more]').hidden = this.rendered >= this.matches.length;
  }
}
if (!customElements.get('luca-gift-finder')) customElements.define('luca-gift-finder', LucaGiftFinder);

if (!customElements.get('luca-gift-results')) customElements.define('luca-gift-results', LucaGiftResults);
